import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Sidebar from '../components/layout/Sidebar';
import MainSidebarContent from '../components/layout/MainSidebarContent';
import TopBar from '../components/layout/TopBar';
import { useAuthStore } from '../stores/authStore';
import api from '../services/api';

interface Chat {
  session_id: string;
  title: string;
  last_turn_at: string;
}

interface Project {
  project_id: string;
  project_name: string;
}

export default function ProjectPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [theme, setTheme] = useState<'dark' | 'light'>('dark');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [project, setProject] = useState<Project | null>(null);
  const [chats, setChats] = useState<Chat[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openMenuChatId, setOpenMenuChatId] = useState<string | null>(null);

  useEffect(() => {
    loadTheme();
    loadProjectData();
  }, [projectId]);

  const loadTheme = () => {
    const saved = localStorage.getItem('fm-theme') as 'dark' | 'light' | null;
    if (saved) setTheme(saved);
  };

  const toggleTheme = () => {
    const next = theme === 'light' ? 'dark' : 'light';
    setTheme(next);
    localStorage.setItem('fm-theme', next);
  };

  const loadProjectData = async () => {
    if (!projectId) return;
    
    try {
      setLoading(true);
      // Load project details
      const projectsRes = await api.get('/api/projects');
      const foundProject = (projectsRes.data.projects || projectsRes.data).find((p: Project) => p.project_id === projectId);
      
      if (!foundProject) {
        setError('Project not found');
        return;
      }
      
      setProject(foundProject);
      
      // Load chats in this project
      const chatsRes = await api.get(`/api/projects/${projectId}/chats`);
      setChats(chatsRes.data.chats || []);
    } catch (err: any) {
      console.error('Failed to load project:', err);
      setError('Failed to load project');
    } finally {
      setLoading(false);
    }
  };

  const handleChatClick = (sessionId: string) => {
    // Set a flag that we're loading a session
    sessionStorage.setItem('loadingSession', sessionId);
    navigate('/', { state: { sessionId } });
  };

  const handleRemoveFromProject = async (sessionId: string) => {
    if (!projectId) return;
    
    try {
      await api.delete(`/api/projects/${projectId}/chats/${sessionId}`);
      setChats((prev) => prev.filter((c) => c.session_id !== sessionId));
      setOpenMenuChatId(null);
    } catch (err) {
      console.error('Failed to remove chat from project:', err);
      alert('Failed to remove chat from project');
    }
  };

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('[data-dropdown-menu]') && !target.closest('[data-menu-button]')) {
        setOpenMenuChatId(null);
      }
    };
    document.addEventListener('click', onClick);
    return () => document.removeEventListener('click', onClick);
  }, []);

  const getRelativeTime = (timestamp: string) => {
    const now = new Date().getTime();
    const then = new Date(timestamp).getTime();
    const diffMs = now - then;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins} min${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} week${Math.floor(diffDays / 7) > 1 ? 's' : ''} ago`;
    return `${Math.floor(diffDays / 30)} month${Math.floor(diffDays / 30) > 1 ? 's' : ''} ago`;
  };

  return (
    <div style={{
      display: 'flex',
      height: '100vh',
      background: 'var(--background)',
      color: 'var(--text)'
    }}>
      <Sidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      >
        <MainSidebarContent 
          currentPage="projects"
          onNewChat={() => navigate('/')}
        />
      </Sidebar>

      <main style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <TopBar
          sidebarOpen={sidebarOpen}
          onToggleSidebar={() => setSidebarOpen(true)}
          theme={theme}
          onToggleTheme={toggleTheme}
          user={user}
        />

        <div style={{
          flex: 1,
          overflowY: 'auto',
          padding: '32px 48px'
        }}>
          {loading ? (
            <div style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0, 0, 0, 0.5)',
              backdropFilter: 'blur(4px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 2000
            }}>
              <div style={{
                background: 'var(--surface)',
                borderRadius: '12px',
                padding: '32px 48px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '16px',
                border: '1px solid var(--border)'
              }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  border: '4px solid var(--border)',
                  borderTop: '4px solid var(--accent)',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }} />
                <div style={{
                  fontSize: '14px',
                  color: 'var(--text-muted)',
                  fontWeight: 500
                }}>
                  Loading project...
                </div>
              </div>
            </div>
          ) : error ? (
            <div style={{ textAlign: 'center', padding: '48px', color: '#ef4444' }}>
              {error}
            </div>
          ) : (
            <>
              {/* Header */}
              <div style={{ marginBottom: '32px' }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  marginBottom: '8px'
                }}>
                  <div style={{
                    width: '48px',
                    height: '48px',
                    background: 'var(--surface2)',
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="2" style={{ width: '24px', height: '24px' }}>
                      <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>
                    </svg>
                  </div>
                  <h1 style={{
                    fontSize: '28px',
                    fontWeight: 600,
                    color: 'var(--text)',
                    margin: 0
                  }}>
                    {project?.project_name}
                  </h1>
                </div>
                <div style={{ color: 'var(--text-muted)', fontSize: '14px', marginLeft: '60px' }}>
                  {chats.length} {chats.length === 1 ? 'chat' : 'chats'}
                </div>
              </div>

              {/* Chats List */}
              {chats.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '48px', color: 'var(--text-muted)' }}>
                  No chats in this project yet
                </div>
              ) : (
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))',
                  gap: '20px'
                }}>
                  {chats.map((chat) => (
                    <div
                      key={chat.session_id}
                      style={{
                        background: 'var(--surface)',
                        border: '1px solid var(--border)',
                        borderRadius: '14px',
                        padding: '20px',
                        position: 'relative',
                        transition: 'border-color 0.15s, transform 0.15s',
                        zIndex: openMenuChatId === chat.session_id ? 9999 : 'auto'
                      }}
                    >
                      <div
                        onClick={() => handleChatClick(chat.session_id)}
                        style={{ cursor: 'pointer' }}
                      >
                        <div style={{
                          fontSize: '16px',
                          color: 'var(--text)',
                          fontWeight: 600,
                          marginBottom: '8px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          paddingRight: '40px'
                        }}>
                          {chat.title || '(no title)'}
                        </div>
                        <div style={{
                          fontSize: '13px',
                          color: 'var(--text-muted)'
                        }}>
                          {getRelativeTime(chat.last_turn_at)}
                        </div>
                      </div>

                      <button
                        data-menu-button="true"
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenMenuChatId(openMenuChatId === chat.session_id ? null : chat.session_id);
                        }}
                        style={{
                          position: 'absolute',
                          top: '16px',
                          right: '16px',
                          background: 'transparent',
                          border: 'none',
                          color: 'var(--text-muted)',
                          cursor: 'pointer',
                          fontSize: '18px',
                          width: '28px',
                          height: '28px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: '6px',
                          transition: 'background-color 0.15s'
                        }}
                        onMouseOver={(e) => {
                          e.currentTarget.style.backgroundColor = 'var(--hover)';
                        }}
                        onMouseOut={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }}
                      >
                        ...
                      </button>

                      {openMenuChatId === chat.session_id && (
                        <div
                          data-dropdown-menu="true"
                          onClick={(e) => e.stopPropagation()}
                          style={{
                            position: 'absolute',
                            top: '48px',
                            right: '16px',
                            background: 'var(--surface)',
                            border: '1px solid var(--border)',
                            borderRadius: '8px',
                            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.2)',
                            minWidth: '180px',
                            zIndex: 10000,
                            overflow: 'hidden'
                          }}
                        >
                          <button
                            onClick={() => handleRemoveFromProject(chat.session_id)}
                            style={{
                              width: '100%',
                              padding: '10px 16px',
                              border: 'none',
                              background: 'transparent',
                              color: 'var(--text)',
                              textAlign: 'left',
                              fontSize: '13px',
                              cursor: 'pointer',
                              transition: 'background-color 0.15s'
                            }}
                            onMouseOver={(e) => {
                              e.currentTarget.style.backgroundColor = 'var(--hover)';
                            }}
                            onMouseOut={(e) => {
                              e.currentTarget.style.backgroundColor = 'transparent';
                            }}
                          >
                            Remove from project
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </main>
    </div>
  );
}
